import { Fragment } from "react";
import { Navigate, NavLink, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { List } from "./list";
import { Button } from "@progress/kendo-react-buttons";
import { formatDateTime } from "../lib/utils";
import { loadEntity } from "../redux/slices/datasetviewpageslice";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

const datasetItemRenderer = (currentTheme, t) => (props, item, index) => {
  const dispatch = useDispatch();
  const handleViewDetails = (e, item) => {
    props.navigate("/datasetview/" + item.Id);
  };

  return (
    <div
      key={item.Id}
      className={`list-item`}
      onDoubleClick={(e) => handleViewDetails(e, item)}
    >
      <div
        className="list-item-title"
        style={{ fontWeight: `${item.Modules != 0 ? 700 : 400}` }}
      >
        <div className="list-detail-title">
          {item.Title} {item.Modules != 0 ? "©" : ""}{" "}
        </div>
        <div className="list-detail-date">
          {/* {formatDateTime(new Date(item.LastUpdatedOnUTC) + "UTC", false)} */}
          {formatDateTime(
            new Date(
              item.LastUpdatedOnUTC.endsWith("Z")
                ? item.LastUpdatedOnUTC
                : `${item.LastUpdatedOnUTC}Z`,
            ),
            false,
          )}
        </div>
      </div>
      <div className="list-item-health list-item-action">
        <Button
          disabled={false}
          style={{
            border: "none",
            color: item.HealthCheck
              ? `${currentTheme.success}`
              : `${currentTheme.error}`,
            backgroundColor: "transparent",
          }}
          icon={item.HealthCheck ? "tick" : "warning"}
        ></Button>
      </div>
      <div className="list-item-from list-item-action">
        <Button
          disabled={false}
          title={
            item.SourceTypeId == "1"
              ? "From DB"
              : item.SourceTypeId == "2"
                ? "From Excel"
                : item.SourceTypeId == "3"
                  ? "From DB Computed"
                  : ""
          }
          style={{
            border: "none",
            color: currentTheme.touchColor,
            backgroundColor: "transparent",
          }}
          icon={
            item.SourceTypeId == "1"
              ? "data"
              : item.SourceTypeId == "2"
                ? "excel"
                : item.SourceTypeId == "3"
                  ? "data-ods"
                  : ""
          }
        ></Button>
      </div>
      <div className="list-item-reccurs list-item-action">
        {item.RecurrsEvery != 0 && (
          <Button
            disabled={false}
            title={`${t("datasetviewer.reccurs")} ${item.RecurrsEvery} ${t(
              "datasetviewer.min",
            )}`}
            style={{
              border: "none",
              color: currentTheme.touchColor,
              backgroundColor: "transparent",
            }}
            icon="clock"
            onClick={(e) => handleViewDetails(e, item)}
          ></Button>
        )}
      </div>
      <div className="list-item-view list-item-action">
        {props.canView && (
          <Button
            disabled={false}
            title={t("datasetviewer.view")}
            style={{
              border: "none",
              color: currentTheme.touchColor,
              backgroundColor: "transparent",
            }}
            icon="eye"
            onClick={(e) => handleViewDetails(e, item)}
          ></Button>
        )}
      </div>

      <div className="list-item-import list-item-action">
        {props.canCreate && item.Modules == 0 && (
          <Button
            disabled={false}
            title={t("datasetviewer.import")}
            style={{
              border: "none",
              color: currentTheme.touchColor,
              backgroundColor: "transparent",
            }}
            icon="import"
            onClick={(e) => {
              item.SourceTypeId == "2"
                ? props.onImport(item.Id, "fromfile")
                : props.onImport(item.Id);
            }}
          ></Button>
        )}
      </div>

      <div className="list-item-delete list-item-action">
        {props.canDelete && item.Modules == 0 && (
          <Button
            disabled={false}
            title={t("datasetviewer.delete")}
            style={{
              border: "none",
              color: currentTheme.error,
              backgroundColor: "transparent",
            }}
            icon="delete"
            onClick={(e) => props.onDelete(item.Id)}
          ></Button>
        )}
      </div>
    </div>
  );
};

export const DatasetsViewer = (props) => {
  const {
    canView,
    canCreate,
    canEdit,
    canDelete,
    onDelete,
    onImport,
    onSetFavorite,
    ...other
  } = props;

  const currentTheme = useSelector((state) => state.currentTheme);
  const currentUser = useSelector((state) => state.currentUser);
  const pageState = useSelector((state) => state.datasetsLocatorPage);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const filter = pageState.filter;
  const showSystemEntities = pageState.showSystemEntities;

  const filteredItems = pageState.items?.filter((e) => {
    if ((!filter || filter.trim() == "") && showSystemEntities) return true;

    if (!filter || filter.trim() == "")
      //do not show system entities
      return e.IsSystem == false;

    //i am here => there is text
    const trimmedFilter = filter?.toLowerCase();

    if (showSystemEntities)
      return e.Title.toLowerCase().indexOf(trimmedFilter) >= 0;
    else
      return (
        e.Title.toLowerCase().indexOf(trimmedFilter) >= 0 && e.IsSystem == false
      );
  });

  return (
    <Fragment>
      <div className={"datasets-viewer"}>
        <div className="header-list">
          <div className="header-left">{t("datasetviewer.title")}</div>
          <div className="header-right">{t("datasetviewer.modified")}</div>
        </div>
        {filteredItems?.length > 0 && (
          <List
            className="datasets-viewer-list"
            navigate={navigate}
            items={filteredItems}
            onSetFavorite={onSetFavorite}
            canView={canView}
            canCreate={canCreate}
            canEdit={canEdit}
            canDelete={canDelete}
            onDelete={onDelete}
            onImport={onImport}
            itemRenderer={datasetItemRenderer(currentTheme, t)}
          />
        )}
      </div>
      <style>
        {`
                    .datasets-viewer
                    {
                        width: 100%;
                        height: 100%;
                        display: flex;
                        flex-wrap: wrap;
                        justify-content: space-between;
                        align-items: flex-start;
                        align-content: start;
                        flex: 1;
                        overflow: auto;
                        padding: 8px 26px;
                    }
                        
                    .datasets-viewer .header-list{
                      display:flex;
                      justify-content:space-between;
                      width:calc(100% - 230px);
                      padding:12px;
                      font-weight:bold;
                    }

                    .datasetslocator-page .datasets-viewer .datasets-viewer-list
                    {
                        border: 1px dotted ${currentTheme.separatorColor}; 
                        border-radius: 8px;
                        min-width: 700px;
                        width: 100%;
                        height: calc(100% - 130px);
                        display:table;
                        padding: 0px 0px 0px 0px;
                        overflow-y:auto;
                    }

                    .datasetslocator-page .datasets-viewer .datasets-viewer-list .list-root
                    {
                        border-collapse: collapse;
                    }

                    .datasetslocator-page .datasets-viewer .datasets-viewer-list .list-item
                    {
                        line-height: 3;
                        border-top: 1px solid ${currentTheme.componentBorderColor}; 
                        border-radius: 6px;
                    }

                    .datasetslocator-page .datasets-viewer .datasets-viewer-list .list-item:first-child
                    {
                        border-top: none;
                    }

                    .datasetslocator-page .datasets-viewer .datasets-viewer-list .list-item:last-child
                    {
                        border-bottom: 1px solid ${currentTheme.componentBorderColor}; 
                    }

                    .datasetslocator-page .datasets-viewer .datasets-viewer-list .list-item:hover
                    {
                      background-color: ${currentTheme.componentBorderColor};
                      cursor: pointer;
                    }

                    .datasetslocator-page .datasets-viewer .datasets-viewer-list .list-item-title,
                    .datasetslocator-page .datasets-viewer .datasets-viewer-list .list-item-system
                    {
                        width: 100%;
                        vertical-align: middle;
                        padding-left: 12px;
                        display:flex;
                        justify-content:space-between;
                    }

                    .datasetslocator-page .datasets-viewer .datasets-viewer-list .list-item-action{
                        width:30px;
                    }

                    @media (max-width: 1024px) {
                      .datasetslocator-mobile-view .datasets-viewer {
                        height: auto;
                        padding: 0;
                        overflow: visible;
                      }

                      .datasetslocator-mobile-view .datasets-viewer .header-list {
                        display: none;
                      }

                      .datasetslocator-mobile-view .datasets-viewer .datasets-viewer-list {
                        min-width: 0;
                        height: auto;
                        border: none;
                        border-radius: 0;
                        display: block;
                        overflow: visible;
                      }

                      .datasetslocator-mobile-view .datasets-viewer .datasets-viewer-list .list-root {
                        display: block;
                      }

                      .datasetslocator-mobile-view .datasets-viewer .datasets-viewer-list .list-item {
                        display: flex;
                        align-items: flex-start;
                        padding: 12px 0;
                        border-top: none;
                        border-bottom: 1px solid ${currentTheme.separatorColor};
                        line-height: normal;
                      }

                      .datasetslocator-mobile-view .datasets-viewer .datasets-viewer-list .list-item > div {
                        display: flex;
                        padding: 0;
                      }

                      .datasetslocator-mobile-view .datasets-viewer .datasets-viewer-list .list-item-title {
                        flex: 1;
                        min-width: 0;
                        flex-direction: column;
                        gap: 6px;
                      }

                      .datasetslocator-mobile-view .datasets-viewer .datasets-viewer-list .list-detail-title {
                        overflow-wrap: anywhere;
                      }

                      .datasetslocator-mobile-view .datasets-viewer .datasets-viewer-list .list-detail-date {
                        font-size: 12px;
                        opacity: 0.75;
                      }

                      .datasetslocator-mobile-view .datasets-viewer .datasets-viewer-list .list-item-action {
                        flex: 0 0 auto;
                        width: auto;
                        margin-inline-start: 4px;
                      }

                      .datasetslocator-mobile-view .datasets-viewer .datasets-viewer-list .list-item-import,
                      .datasetslocator-mobile-view .datasets-viewer .datasets-viewer-list .list-item-delete {
                        display: none !important;
                      }
                    }
                    
                    `}
      </style>
    </Fragment>
  );
};
